import { Button } from "@repo/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import { Input } from "@repo/ui/input";
import { Label } from "@repo/ui/label";
import { Switch } from "@repo/ui/switch";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface ReparseResult {
  snapshotId: string;
  sourceUrl: string;
  capturedAt: string;
  contentType: string | null;
  parsed: Record<string, unknown>;
  before: Record<string, unknown> | null;
  diff: string[];
  applied: boolean;
  /** Present with extractAssets: what the stored page offers vs what the property holds. */
  assets?: {
    offered: number;
    offeredPhotos: number;
    held: number;
    missing: number;
    missingPhotos: number;
    saved?: number;
    skipped?: number;
    failed?: number;
    failureReasons?: Record<string, number>;
  };
  error?: string;
}

interface ReparseResponse {
  success: boolean;
  processed: number;
  applied: number;
  assetsSaved?: number;
  /** captured_at of the oldest row when the page was full: preview older pages from here. */
  nextUntil?: string | null;
  mode: "preview" | "apply";
  adapterSlug: string;
  results: ReparseResult[];
  error?: string;
}

/** A previewed row apply would change: a field diff, or assets the stored page can supply. */
function needsApply(row: ReparseResult): boolean {
  return !row.error && (row.diff.length > 0 || (row.assets?.missing ?? 0) > 0);
}

function describeAssets(assets: NonNullable<ReparseResult["assets"]>): string {
  const base = `photos: ${assets.offeredPhotos} on stored page, ${assets.missingPhotos} missing; ${assets.missing} asset(s) to recover`;
  if (assets.saved === undefined) return base;
  const reasons = Object.entries(assets.failureReasons ?? {})
    .map(([reason, n]) => `${reason} ${n}`)
    .join(", ");
  return `${base} → saved ${assets.saved}, skipped ${assets.skipped ?? 0}, failed ${assets.failed ?? 0}${reasons ? ` (${reasons})` : ""}`;
}

export function ReparseSnapshotsCard({ adapterSlug }: { adapterSlug: string }) {
  const { toast } = useToast();
  const { canManageJobs } = useAuth();
  const [since, setSince] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [limit, setLimit] = useState<number>(25);
  const [recoverAssets, setRecoverAssets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ReparseResponse | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const run = async (mode: "preview" | "apply", until?: string) => {
    setLoading(true);
    setResponse(null);
    setRequestError(null);
    try {
      const { data, error } = await supabase.functions.invoke<ReparseResponse>(
        "reparse-snapshots",
        {
          body: {
            adapterSlug,
            since: since ? new Date(since).toISOString() : undefined,
            until,
            limit,
            mode,
            extractAssets: recoverAssets,
            snapshotIds: mode === "apply" ? response?.results.filter(needsApply).map((row) => row.snapshotId) : undefined,
          },
        },
      );
      if (error) {
        let serverMessage: string | undefined;
        if (error instanceof FunctionsHttpError) {
          try {
            const payload: unknown = await error.context.json();
            if (payload && typeof payload === "object" && "error" in payload &&
                typeof payload.error === "string") serverMessage = payload.error;
          } catch {
            // Keep the transport error when the response has no JSON body.
          }
        }
        throw new Error(serverMessage ?? error.message);
      }
      if (!data) throw new Error("Empty response from reparse-snapshots");
      if (!data.success) throw new Error(data.error ?? "reparse-snapshots failed");
      setResponse(data);
      toast({
        title: mode === "apply" ? "Reparse applied" : "Reparse preview",
        description:
          `${data.processed} snapshot(s) processed; ${data.applied} row(s) updated` +
          (recoverAssets
            ? mode === "apply"
              ? `; ${data.assetsSaved ?? 0} asset(s) recovered.`
              : `; ${data.results.reduce((sum, r) => sum + (r.assets?.missing ?? 0), 0)} asset(s) recoverable.`
            : "."),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setRequestError(message);
      toast({ title: "Reparse failed", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <RefreshCw className="h-4 w-4" />
          Reparse stored snapshots
        </CardTitle>
        <CardDescription>
          Bulk maintenance: compare parser-derived column patches with saved snapshots without
          re-scraping. Apply updates only the snapshot IDs in the preview. For the merged object
          shape and field sources, use Parse stored page in Discover &amp; sample. With photo
          recovery on, the stored page also supplies photos the property lacks: apply downloads
          them from the image CDN, never refetching the listing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="reparse-since">Since</Label>
            <Input
              id="reparse-since"
              type="date"
              value={since}
              onChange={(e) => { setSince(e.target.value); setResponse(null); }}
              disabled={loading || !canManageJobs}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="reparse-limit">Limit</Label>
            <Input
              id="reparse-limit"
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => { setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 25))); setResponse(null); }}
              disabled={loading || !canManageJobs}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => run("preview")}
              disabled={loading || !canManageJobs}
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Preview
            </Button>
            <Button
              type="button"
              onClick={() => run("apply")}
              disabled={loading || !canManageJobs || !response || response.mode !== "preview" || !response.results.some(needsApply)}
            >
              Apply
            </Button>
            {response?.mode === "preview" && response.nextUntil ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => run("preview", response.nextUntil ?? undefined)}
                disabled={loading || !canManageJobs}
              >
                Older
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="reparse-recover-assets"
            checked={recoverAssets}
            onCheckedChange={(checked) => { setRecoverAssets(checked); setResponse(null); }}
            disabled={loading || !canManageJobs}
          />
          <Label htmlFor="reparse-recover-assets" className="text-sm font-normal">
            Also recover photos from stored pages
          </Label>
        </div>
        {!canManageJobs && <p className="text-xs text-muted-foreground">Operator role required to preview or apply a bulk reparse.</p>}
        {requestError && <p className="text-xs text-destructive">{requestError}</p>}
        {response ? (
          <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
            <p className="font-medium">
              {response.processed} processed · {response.applied} applied ·
              {response.assetsSaved ? ` ${response.assetsSaved} assets recovered ·` : ""}{" "}
              mode={response.mode}
            </p>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {response.results.map((r) => (
                <div key={r.snapshotId} className="border-b last:border-0 pb-1">
                  <p className="font-mono truncate">{r.sourceUrl}</p>
                  {r.error ? (
                    <p className="text-destructive">⚠ {r.error}</p>
                  ) : !needsApply(r) && !r.assets ? (
                    <p className="text-muted-foreground">no changes</p>
                  ) : (
                    <>
                      {r.diff.length > 0 ? (
                        <ul className="list-disc pl-5">
                          {r.diff.map((line) => (
                            <li key={line} className="font-mono">{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted-foreground">no field changes</p>
                      )}
                      {r.assets ? (
                        <p className={r.assets.missing > 0 ? "" : "text-muted-foreground"}>
                          {describeAssets(r.assets)}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
